
import '@watergis/maplibre-gl-terradraw/dist/maplibre-gl-terradraw.css'
import { MaplibreTerradrawControl, MaplibreMeasureControl } from '@watergis/maplibre-gl-terradraw';
import { Map, useControl, useMap, Popup, Source, Layer } from 'react-map-gl/maplibre';
import { useEffect, useRef, useState } from 'react';
import { sample } from '@turf/turf';
import {
    addDoc,
    getDocs,
    setDoc,
    collection,
    getFirestore,
    onSnapshot,
    doc,
    deleteDoc,
} from "firebase/firestore";
import { db, auth } from '../../firebase/firebase';
import { Row, Col, Form } from 'react-bootstrap';
import { Events } from '../../firebase/telemetry';
import { addWaypointPins, raiseWaypointPinLayer, waypointLayerLayout, waypointGlyphLayerLayout, WAYPOINT_PIN_LAYER, WAYPOINT_GLYPH_LAYER, WAYPOINT_SOURCE, WAYPOINT_COLOURS, DEFAULT_WAYPOINT_COLOUR } from './waypointPin';
import { WaypointHint } from './WaypointHint';

export const sampleData = [
    {
        "id": "775084a3-eb6a-4aca-8674-46427d874da2",
        "type": "Feature",
        "geometry": {
            "type": "Point",
            "coordinates": [
                121.332886124,
                -30.678422551
            ]
        },
        "properties": {
            "mode": "point"
        }
    },
    {
        "id": "16c380e0-08a0-471d-b147-b38d204bb51a",
        "type": "Feature",
        "geometry": {
            "type": "Point",
            "coordinates": [
                121.326206076,
                -30.686540341
            ]
        },
        "properties": {
            "mode": "point"
        }
    },
    {
        "id": "be0c30db-7d8a-4c64-bbd4-19954071311f",
        "type": "Feature",
        "geometry": {
            "type": "Point",
            "coordinates": [
                121.345520128,
                -30.689162865
            ]
        },
        "properties": {
            "mode": "point"
        }
    },
    {
        "id": "3fdecd27-ee81-4648-9b88-6a8b5ef1d921",
        "type": "Feature",
        "geometry": {
            "type": "Point",
            "coordinates": [
                121.331724376,
                -30.696405658
            ]
        },
        "properties": {
            "mode": "point"
        }
    },
    {
        "id": "0d859268-e881-44fa-b6ca-8727ca9b808e",
        "type": "Feature",
        "geometry": {
            "type": "Point",
            "coordinates": [
                121.339566172,
                -30.696905141
            ]
        },
        "properties": {
            "mode": "point"
        }
    },
    {
        "id": "27024e00-f160-45d0-a34d-6d90027cb9f6",
        "type": "Feature",
        "geometry": {
            "type": "Point",
            "coordinates": [
                121.347698405,
                -30.699402517
            ]
        },
        "properties": {
            "mode": "point"
        }
    }
]

export const arrayToObject = (array, keyField) =>
    array.reduce((obj, item) => {
        obj[item[keyField]] = item
        return obj
    }, {})




export function DrawControl({ drawCtrl, drawpointMode, overlayVisibility, onModeChange, onStoreChange }) {

    // Which draw mode is armed, mirrored out of TerraDraw so the hint banner
    // can report it. TerraDraw has no "mode changed" event, so this is set at
    // every point we change the mode ourselves plus on its finish event.
    const { goldfindermap } = useMap(); // `goldfindermap` is the id on <Map>

    const [mode, setMode] = useState(null);
    // useMap() hands back the MapLibre map itself, not a react-map-gl ref, so
    // it is used directly - as every other overlay in this app does. Calling
    // .getMap() on it returns undefined, which silently disabled the pin layer:
    // the poll below spun 40 times against `undefined` and gave up, so the
    // symbol layer was never added and clicking a pin found nothing.
    const mapOf = (m) => (m && typeof m.getStyle === 'function' ? m : m?.getMap?.());

    const [savedAt, setSavedAt] = useState(0);

    // What the pins are drawn from. Kept here rather than read off TerraDraw at
    // render time so the layer exists from the first paint - TerraDraw only
    // creates its own source once it has a feature, which is exactly when it is
    // too late to hang a layer off it.
    const [waypointFc, setWaypointFc] = useState({ type: 'FeatureCollection', features: [] });

    // The pin layer needs the id back when someone clicks it, and MapLibre
    // drops the UUID string ids TerraDraw uses, so it rides in properties.
    const toFc = (features) => ({
        type: 'FeatureCollection',
        features: (features || [])
            .filter(f => f?.geometry?.type === 'Point')
            .map(f => ({ ...f, properties: { ...f.properties, gfid: f.id } })),
    });

    /**
     * Loads saved waypoints out of Firestore and into TerraDraw.
     *
     * This used to fire once, 100ms after activate(), behind an
     * `if (drawInstance.enabled)` guard - and if TerraDraw had not finished
     * starting by then it silently did nothing and never tried again. That is
     * why saved waypoints came back on some loads and not others.
     *
     * Now it waits for the instance to actually be ready before adding, and
     * skips ids already in the store so a second call cannot double-add
     * (addFeatures throws on a duplicate id).
     */
    const loadData = async () => {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        const markerStorageLocation = collection(db, "user_info", userId, "placemarks");

        let stored = [];
        try {
            const placemarks = await getDocs(markerStorageLocation);
            placemarks.forEach(mark => stored.push(mark.data()));
        } catch (e) {
            console.warn('Could not load waypoints', e);
            return;
        }
        if (!stored.length) return;

        // Render straight away from what Firestore gave us. TerraDraw ingests
        // the same features below for editing, but the pins must not wait on it.
        setWaypointFc(toFc(stored));

        // Wait for TerraDraw to be ready rather than guessing a delay.
        for (let i = 0; i < 50; i++) {
            const inst = drawCtrl.getTerraDrawInstance();
            if (inst && inst.enabled) {
                const existing = new Set(inst.getSnapshot().map(f => f.id));
                const fresh = stored.filter(f => f.id && !existing.has(f.id));
                if (fresh.length) {
                    try {
                        inst.addFeatures(fresh);
                    } catch (e) {
                        console.warn('Could not restore waypoints', e);
                    }
                }
                return;
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise(r => setTimeout(r, 100));
        }
    }

    useEffect(() => {
        if (!overlayVisibility.waypoints) {
            drawCtrl.deactivate();
            setMode(null);
            return undefined;
        }

        drawCtrl.activate();
        // Small delay to ensure Terra Draw is fully activated before loading data
        const dataTimer = setTimeout(() => loadData(), 100);

        const map = mapOf(goldfindermap);

        // Two things have to be redone whenever the style changes:
        //
        // 1. Register the pin images. Doing it once when the control is added
        //    is too early - the style is not ready, addImage is skipped, and
        //    the symbol layer ends up with nothing to draw. addWaypointPins is
        //    idempotent, so calling it again is free.
        // 2. Raise the pin layer. DrawControl mounts early inside <Map>, so
        //    react-map-gl inserts the layer UNDER the base imagery, where it
        //    is invisible but still hit-testable.
        const sync = () => {
            addWaypointPins(map);
            raiseWaypointPinLayer(map);
        };
        sync();
        map?.on?.('styledata', sync);
        map?.on?.('idle', sync);

        return () => {
            clearTimeout(dataTimer);
            map?.off?.('styledata', sync);
            map?.off?.('idle', sync);
        };
    }, [overlayVisibility.waypoints, goldfindermap])


    useControl(() => drawCtrl, // create
        (e) => {
            // Data loading is handled in the useEffect hook when the waypoints
            // overlay is enabled. What has to happen here is the pin artwork:
            // register it up front so the symbol layer has an image the moment
            // TerraDraw creates its source.
            addWaypointPins(e.map);
        }, // ,add
        (map, lib) => { }, // remove
        {
            position: 'top-left'
        }

    );





    useEffect(() => {
        // The instance does not exist until useControl has added the control to
        // the map, so this effect can run before there is anything to listen
        // to. Bailing out would be worse than throwing: with [] deps this only
        // runs once, so the change listener would never attach and NOTHING
        // would ever be saved to Firestore. Wait for it instead.
        let cancelled = false;
        let instance = null;

        const attach = (inst) => {
            instance = inst;

            inst.on("change", (ids, type) => {
                //Done editing
                const snapshot = instance.getSnapshot();
                // Mirror TerraDraw's store into the source the pins render
                // from, so placing, dragging and deleting all show up.
                setWaypointFc(toFc(snapshot));
                // The sidebar reads the same store for its count and export,
                // and TerraDraw's store is not React state - without this a
                // delete would leave both stale.
                onStoreChange?.();


            const userId = auth.currentUser?.uid;

            if (type === "delete") {
                // delete the points from the store
                ids.forEach(id => {
                    const markerStorageLocation = doc(
                        db,
                        "user_info",
                        userId,
                        "placemarks",
                        id
                    );

                    deleteDoc(markerStorageLocation);
                })

            } else {
                ids.forEach(id => {
                    const markerStorageLocation = doc(
                        db,
                        "user_info",
                        userId,
                        "placemarks",
                        id
                    );

                    const docRef = setDoc(markerStorageLocation,
                        instance.getSnapshotFeature(id)
                    );
                    // Geometry type only — never the coordinates, which are
                    // the user's own targets.
                    Events.waypointSaved();
                });
            }
        });


            inst.on("finish", (id, context) => {
            if (context.action === 'draw') {
                // Placing ends the tool. Leaving point mode armed meant a stray
                // click anywhere afterwards dropped another marker, and moving
                // one now lives in its own popup rather than in a mode you are
                // silently left sitting in.
                // resetActiveMode, not setMode: it clears the toolbar's own
                // .active classes as well as changing the mode. setMode alone
                // left the button lit, so the tool looked armed when it was not.
                try {
                    drawCtrl.resetActiveMode();
                } catch { /* instance torn down */ }
                setMode(null);
                setSavedAt(Date.now());
            } else if (context.action === 'dragFeature') {
                // Do something for a drag finish event
            } else if (context.action === 'dragCoordinate') {
                //
            } else if (context.action === 'dragCoordinateResize') {
                //
            }
            });
        };

        const ready = drawCtrl.getTerraDrawInstance();
        if (ready) {
            attach(ready);
        } else {
            const timer = setInterval(() => {
                const inst = drawCtrl.getTerraDrawInstance();
                if (cancelled || !inst) return;
                clearInterval(timer);
                attach(inst);
            }, 100);
            return () => { cancelled = true; clearInterval(timer); };
        }
        return undefined;
    }, [drawCtrl])

    // Keep `mode` in step with the toolbar. The control emits `mode-changed`
    // whenever a tool button is pressed, which is authoritative - reading the
    // DOM classes ourselves would only re-derive what it already knows.
    useEffect(() => {
        if (!overlayVisibility.waypoints) return undefined;

        const read = () => {
            const active = document.querySelector('.maplibregl-terradraw-add-control.active');
            if (!active) return setMode(null);
            if (active.classList.contains('maplibregl-terradraw-add-point-button')) return setMode('point');
            return setMode(null);
        };

        read();
        try {
            drawCtrl.on('mode-changed', read);
        } catch { /* older control build without the event */ }
        return () => { try { drawCtrl.off?.('mode-changed', read); } catch { /* no-op */ } };
    }, [overlayVisibility.waypoints, drawCtrl]);

    // Escape leaves the armed tool. Without it the only way out is to find the
    // same small button again and toggle it off.
    useEffect(() => {
        if (!mode) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            try {
                drawCtrl.resetActiveMode();
            } catch { /* instance not ready */ }
            setMode(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mode, drawCtrl]);

    // The cursor is NOT set here. react-map-gl writes its `cursor` prop onto the
    // same canvas as an inline style, and mouse-move updates that constantly,
    // so anything written directly here was overwritten within a frame. The map
    // resolves draw mode into the cursor itself - see mapCursor in
    // gl-map-optimized - and this just reports the mode up to it.
    useEffect(() => {
        onModeChange?.(mode);
    }, [mode, onModeChange]);

    const cancel = () => {
        try {
            drawCtrl.resetActiveMode();
        } catch { /* instance not ready */ }
        setMode(null);
    };

    if (!overlayVisibility.waypoints) return null;

    return (
        <>
            {/* Our own source, so the pins exist from the first paint and can be
                hit-tested. TerraDraw still owns placing, dragging and deleting;
                the change handler mirrors its store into here. */}
            <Source id={WAYPOINT_SOURCE} type="geojson" data={waypointFc}>
                <Layer id={WAYPOINT_PIN_LAYER} type="symbol" layout={waypointLayerLayout} />
                {/* The icon rides above the coloured body on its own layer, so
                    colour and icon vary independently instead of needing an
                    image per combination. Hit-testing stays on the pin layer:
                    the two cover the same ground, and querying both would return
                    every waypoint twice. */}
                <Layer id={WAYPOINT_GLYPH_LAYER} type="symbol" layout={waypointGlyphLayerLayout} />
            </Source>
            <WaypointHint mode={mode} saved={savedAt} onCancel={cancel} />
        </>
    );
}

function DelayedStringInputField({ setValue, value, label }) {
    const [query, setQuery] = useState(value);

    useEffect(() => {
        if (query !== value) {

            const timeOutId = setTimeout(() => setValue(query), 200);
            return () => clearTimeout(timeOutId);
        }
    }, [query]);


    useEffect(() => {
        if (query !== value) {
            setQuery(value);
        }
    }, [value]);

    return (
        <>
            <Form.Label column="sm"  >{label}</Form.Label>
            <Form.Control
                size="sm"
                type="text"
                value={query}
                onChange={event => setQuery(event.target.value)}
            />
        </>
    );
}

function DelayedTextAreaInputField({ setValue, value, label }) {
    const [query, setQuery] = useState(value);

    useEffect(() => {
        if (query !== value) {

            const timeOutId = setTimeout(() => setValue(query), 200);
            return () => clearTimeout(timeOutId);
        }
    }, [query]);


    useEffect(() => {
        if (query !== value) {
            setQuery(value);
        }
    }, [value]);

    return (
        <>
            <Form.Label column="sm"  >{label}</Form.Label>
            <Form.Control
                as="textarea"
                rows={4}
                size="sm"
                type="text"
                value={query}
                onChange={event => setQuery(event.target.value)}
            />
        </>
    );
}

//


export function CreateDrawPointPopup({ feature, latLng, setClosed, drawctrl }) {

    const [name, setName] = useState(feature.properties.name);
    const [desc, setDesc] = useState(feature.properties.description);
    const [colour, setColour] = useState(feature.properties.colour || DEFAULT_WAYPOINT_COLOUR);
    const [moving, setMoving] = useState(false);

    // updateFeatureProperties is a shallow merge in place. The old code did
    // removeFeatures + addFeatures for every keystroke, which fires the change
    // handler twice - once as a delete, once as a create - and raced Firestore
    // against itself deleting and re-writing the same doc.
    const setProp = (patch) => {
        try {
            drawctrl.getTerraDrawInstance()?.updateFeatureProperties(feature.id, patch);
        } catch (e) {
            console.warn('Could not update waypoint', e);
        }
    };

    const updateName = (value) => { setName(value); setProp({ name: value }); };
    const updateDesc = (value) => { setDesc(value); setProp({ description: value }); };

    const updateColour = (key) => {
        setColour(key);
        setProp({ colour: key });
    };

    const deleteFeat = (id) => {
        drawctrl.getTerraDrawInstance()?.removeFeatures([id]);
        setClosed(false);
    };

    // Moving is per-marker and opt-in. Select mode makes every marker draggable
    // at once, which is how people used to move the wrong one by accident;
    // selecting this feature explicitly scopes it to the marker in hand.
    const startMove = () => {
        try {
            drawctrl.getTerraDrawInstance()?.selectFeature(feature.id);
            setMoving(true);
        } catch (e) {
            console.warn('Could not start move', e);
        }
    };

    const finishMove = () => {
        try {
            drawctrl.getTerraDrawInstance()?.deselectFeature(feature.id);
            drawctrl.resetActiveMode();
        } catch { /* already gone */ }
        setMoving(false);
        setClosed(false);
    };

    return <Popup latitude={latLng.lat} longitude={latLng.lng}
        anchor="bottom"
        maxWidth="270px"
        onClose={() => setClosed(false)}>

        {moving ? (
            <div style={wpStyles.moving}>
                <div style={wpStyles.movingTitle}>Drag the marker where you want it</div>
                <button type="button" style={wpStyles.primary} onClick={finishMove}>Done</button>
            </div>
        ) : (
            <>
                <Row className='mb-1'>
                    <Col>
                        <DelayedStringInputField setValue={updateName} value={name} label='Name' />
                    </Col>
                </Row>
                <Row className='mb-1'>
                    <Col>
                        <DelayedTextAreaInputField setValue={updateDesc} value={desc} label='Notes' />
                    </Col>
                </Row>

                <div style={wpStyles.label}>Colour</div>
                <div style={wpStyles.swatches}>
                    {WAYPOINT_COLOURS.map((c) => (
                        <button
                            key={c.key}
                            type="button"
                            title={c.label}
                            aria-label={c.label}
                            aria-pressed={colour === c.key}
                            onClick={() => updateColour(c.key)}
                            style={{
                                ...wpStyles.swatch,
                                background: c.hex,
                                outline: colour === c.key ? `2px solid ${c.hex}` : 'none',
                                outlineOffset: 2,
                            }}
                        />
                    ))}
                </div>

                <div style={wpStyles.coords}>
                    {feature.geometry.coordinates[1].toFixed(5)}, {feature.geometry.coordinates[0].toFixed(5)}
                </div>

                <div style={wpStyles.actions}>
                    <button type="button" style={wpStyles.secondary} onClick={startMove}>Move</button>
                    <button type="button" style={wpStyles.danger} onClick={() => deleteFeat(feature.id)}>Delete</button>
                </div>
            </>
        )}
    </Popup >
}

const wpStyles = {
    label: {
        fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
        color: 'var(--gf-secondary-500, #4B5563)', margin: '8px 0 5px',
    },
    swatches: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4 },
    swatch: {
        width: 20, height: 20, borderRadius: '50%', border: '1.5px solid #fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.25)', cursor: 'pointer', padding: 0, flex: 'none',
    },
    coords: {
        fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
        fontVariantNumeric: 'tabular-nums', fontSize: 11,
        color: 'var(--gf-secondary-500, #4B5563)', margin: '10px 0 8px',
    },
    actions: { display: 'flex', gap: 8 },
    secondary: {
        flex: 1, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
        background: 'var(--gf-primary-50, #FFF4ED)', color: 'var(--gf-primary-500, #E65A00)',
        border: '1px solid var(--gf-primary-400, #FD7218)',
    },
    danger: {
        flex: 1, height: 32, borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
        background: '#fff', color: '#B42318', border: '1px solid #F0C4BF',
    },
    primary: {
        width: '100%', height: 34, borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
        background: 'var(--gf-primary-400, #FD7218)', color: '#fff', border: 0,
    },
    moving: { padding: '2px 0' },
    movingTitle: { fontSize: 13, marginBottom: 10, color: 'var(--gf-secondary-700, #2D3238)' },
};

